<template>
    <div class="preparation-product">
        <div class="card" :class="[ preparationBorderClass(preparationStatus.color) ]">
            <div class="card-body">
                <div class="preparation-product-header">
                    <div class="preparation-product-ordered" :class="{ 'bg-danger': counters.nb_ordered <= 0 && !orderIsPickupPointProduction}">
                        {{ counters.nb_ordered }}
                    </div>

                    <div class="preparation-product-name-pricetype">
                        <slot></slot>
                        <div class="preparation-product-pricetype">
                            {{ isWeightNeeded ? "au kg" : "à l'unité" }}
                            <template v-if="counters.nb_offered > 0">
                                - {{ counters.nb_offered }} <i class="fas fa-gift"></i>
                            </template>
                        </div>
                    </div>

                    <div class="preparation-product-prepared" :class="preparationBgClass(preparationStatus.color)">
                        {{ counters.nb_accounted }}
                    </div>
                </div>

                <div v-if="counters.nb_toremove" class="alert alert-danger">
                    <i class="fa-duotone fa-exclamation-triangle"></i> Commande modifiée : il faut retirer {{ counters.nb_toremove }} {{ pluralize('produit', counters.nb_toremove) }} {{ pluralize('préparé', counters.nb_toremove) }}.
                </div>

                <div class="preparation-product-inputs">
                    <table>
                        <template v-if="inputs.length === 0">
                            <tr>
                                <td class="text-center font-weight-bold">Aucun produit préparé</td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr v-for="input in inputs" class="preparation-product-input">
                                <template v-if="input.type === 'missing'">
                                    <td v-if="input.type === 'missing'" colspan="2" class="preparation-product-input-missing">
                                        <Modal>
                                            <template #button="sp">
                                                <button class="btn btn-outline-dark" type="button" @click="sp.showModal()">
                                                    <MissingProductsIcon></MissingProductsIcon>
                                                    {{ input.quantity }}
                                                    <small>
                                                        manquant(s)
                                                        <template v-if="orderHasSubcustomers">
                                                            pour
                                                            {{ getSubcustomerOrderRefToDisplay( subcustomersWithOrderById[input.fk_subcustomer]) }}
                                                        </template>
                                                    </small>
                                                </button>
                                            </template>
                                            <template #body>
                                                <div class="text-left">
                                                    <template v-if="orderHasSubcustomers">
                                                        Commande
                                                        {{ subcustomersWithOrderById[input.fk_subcustomer].order.ref }}
                                                        <br>
                                                        Client : {{ subcustomersWithOrderById[input.fk_subcustomer].name }}
                                                        <br>
                                                    </template>
                                                    {{ input.quantity }} <i>{{ product.name }}</i> manquant(s)<br>
                                                    Raison: {{ input.reason }}
                                                </div>
                                            </template>
                                            <template #footer="sp">
                                                <button type="button" class="btn btn-outline-dark" @click="sp.closeModal()">
                                                    Fermer
                                                </button>
                                            </template>
                                        </Modal>
                                    </td>
                                </template>
                                <template v-else>
                                    <td class="preparation-product-input-quantity" :colspan="isWeightNeeded ? 1 : 2">
                                        {{ input.quantity }}
                                        <small class="text-muted">unité(s)</small>
                                    </td>
                                    <template v-if="isWeightNeeded">
                                        <td v-if="input.type === 'prepared'" class="preparation-product-input-weight">
                                            {{ formatWeight(input.weight) }}
                                        </td>
                                    </template>
                                </template>

                                <td class="preparation-product-input-actions text-right">
                                    <button v-if="orderEditable && input.type === 'prepared'" class="btn btn-dark" @click="removePrepared(input.id)" :disabled="deleting">
                                        <i class="fas fa-times"></i>
                                    </button>
                                    <button v-if="orderEditable && input.type === 'missing'" class="btn btn-dark" @click="removeMissing(input.id)" :disabled="deleting">
                                        <i class="fas fa-times"></i>
                                    </button>
                                </td>
                            </tr>
                        </template>
                    </table>
                </div>

                <div v-if="!order.ready_to_prepare" class="alert alert-warning">
                    <NotReadyToPrepareIcon/> Cette commande n'est pas encore préparable
                </div>

                <div v-if="!readOnly" class="preparation-product-footer">
                    <errors-zone class="mb-2" :errors="errorsSending" />
                    <errors-zone class="mb-2" :errors="errorsDeleting" />

                    <div v-if="returnedQuantity" class="preparation-product-returned float-left">
                        {{ returnedQuantity }} {{ pluralize('retour', returnedQuantity) }} <i class="fa-duotone fa-arrow-turn-down-left"></i>
                    </div>

                    <AddPreparedProductsForm v-if="(counters.nb_pending > 0 || orderIsPickupPointProduction) && orderEditable"
                        :order="order"
                        :product="product"
                        :display-weight="isWeightNeeded"
                        :disabled="deleting"
                        @errors="onAddPreparedErrors"
                        @submitted="onAddPreparedSubmitted"
                    />

                    <div class="dropdown float-right">
                        <button class="btn btn-secondary dropdown-toggle" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <i class="fad fa-list"></i>
                        </button>
                        <div class="dropdown-menu">
                            <button v-if="orderProductsQuantitiesEditable" class="dropdown-item" @click="$refs.modalChangeQuantity.showModal()">
                                <i class="fad fa-fw fa-user-lock"></i> Modifier la quantité commandée
                            </button>

                            <button v-if="orderProductsQuantitiesEditable" class="dropdown-item" @click="$refs.modalChangeQuantityOffered.showModal()">
                                <i class="fad fa-fw fa-user-lock"></i> Modifier la quantité offerte
                            </button>

                            <template v-if="displayAddMissingProductsModal && counters.nb_pending > 0 && orderEditable">
                                <button v-if="orderEditable" class="dropdown-item" @click="$refs.modalAddMissing.showModal()">
                                    <i class="fad fa-fw fa-user-lock"></i> Marquer manquant
                                </button>
                            </template>

                            <button class="dropdown-item" @click="$refs.modalOrderHistory.showModal()">
                                <i class="fad fa-fw fa-clock"></i> Voir l'historique
                            </button>
                        </div>
                    </div>

                    <OrderHistoryModal
                        ref="modalOrderHistory"
                        :order="order"
                        :product="product"
                    />

                    <component
                        v-if="displayAddMissingProductsModal"
                        :is="displaySubcustomers
                                ? 'AddMissingProductsWithSubcustomersModal'
                                : 'AddMissingProductsModal'"
                        ref="modalAddMissing"
                        :product-sku="product.sku"
                        :product-name="product.tiny_name"
                        :order="order"
                        :subcustomers-orders="subcustomersOrders"
                        :cancellation-reasons="cancellationReasons"
                    />

                    <ChangeNbProductsOrderedModal
                        v-if="orderProductsQuantitiesEditable"
                        ref="modalChangeQuantity"
                        :product-sku="product.sku"
                        :product-name="product.tiny_name"
                        :initial-quantity="counters.nb_ordered"
                        :quantity-offered="counters.nb_offered"
                        :order="order"
                    />

                    <ChangeNbProductsOfferedModal
                        v-if="orderProductsQuantitiesEditable"
                        ref="modalChangeQuantityOffered"
                        :product-sku="product.sku"
                        :product-name="product.tiny_name"
                        :ordered-quantity="counters.nb_ordered"
                        :initial-quantity="counters.nb_offered"
                        :order="order"
                    />
                </div>
            </div>

            <div v-if="displaySubcustomers">
                <ul v-if="orderHasSubcustomers" class="list-group">
                    <li class="list-group-item list-group-item-dark font-weight-bold">Commandes</li>
                    <template v-for="(pickupPointSubcustOrders, pickupPointName) in subcustomersOrdersByPickupPoint">
                        <li
                            v-if="displayPickupPointsNames"
                            class="list-group-item list-group-item-info font-weight-bold"
                        >
                            {{ pickupPointName }}
                        </li>
                        <li
                            v-for="custOrder in pickupPointSubcustOrders" :key="custOrder.subcustomer.id"
                            class="list-group-item list-group-item-suborder d-flex"
                            :class="custOrder.quantity > 1 ? 'text-underline font-weight-bold' : ''"
                        >
                            <div class="list-group-item-suborder-ref text-nowrap cursor-help" v-if="!custOrder.ref || custOrder.ref.startsWith('(')"
                                 v-on:click="global.alert(titleNoSubOrderNumber)">
                              n°<strong class="bg-light border border-danger px-1 ml-1 rounded text-danger">
                                <i class="fad fa-exclamation-triangle" :title="titleNoSubOrderNumber"></i>
                                <span>Inconnu</span>
                              </strong>
                            </div>
                            <div class="list-group-item-suborder-ref text-nowrap" v-else>
                              n°<strong class="bg-light border border-dark px-1 ml-1 rounded">{{ custOrder.ref }}</strong>
                            </div>
                            <div class="list-group-item-suborder-name text-ellipsis">
                                {{ custOrder.subcustomer.name }}
                            </div>
                            <div class="list-group-item-suborder-quantity align-self-end font-weight-bolder">
                                x{{ custOrder.quantity }}
                                <template v-if="nbMissingByOrderRef[custOrder.ref]">
                                    <small>({{ nbMissingByOrderRef[custOrder.ref] }} manq.)</small>
                                </template>
                            </div>
                        </li>
                    </template>
                </ul>
                <div v-else class="text-center">Aucun client trouvé</div>
            </div>
        </div>
    </div>
</template>

<script>
    import ErrorsZone from "../ErrorsZone";
    import OrderHistoryModal from "../OrderHistoryModal"
    import AddMissingProductsModal from "./AddMissingProductsModal";
    import AddMissingProductsWithSubcustomersModal from "./AddMissingProductsWithSubcustomersModal"
    import MissingProductsIcon from "./MissingProductsIcon"
    import NotReadyToPrepareIcon from "./NotReadyToPrepareIcon";
    import ChangeNbProductsOrderedModal from "./ChangeNbProductsOrderedModal";
    import ChangeNbProductsOfferedModal from "./ChangeNbProductsOfferedModal"
    import AddPreparedProductsForm from "./AddPreparedProductsForm";
    import Modal from "../Modal";

    import {useDeleteRequest} from "../compositions/axios/deleteRequest";
    import {pluralize} from "../../helpers";
    import {orderEditable, orderProductsQuantitiesEditable} from "../../orders-utils";
    import {preparationBgClass, preparationBorderClass} from "../../preparation-classes";

    export default {
        name: 'PreparationProduct',

        components: {
            MissingProductsIcon,
            NotReadyToPrepareIcon,
            ErrorsZone,
            OrderHistoryModal,
            AddMissingProductsModal,
            AddMissingProductsWithSubcustomersModal,
            AddPreparedProductsForm,
            ChangeNbProductsOrderedModal,
            ChangeNbProductsOfferedModal,
            Modal,
        },

        props: {
            order: {
                type: Object,
                required: true,
            },
            product: {
                type: Object,
                required: true,
            },
            preparationStatus: {
                type: Object,
                required: true,
            },
            inputs: {
                type: Array,
                required: true,
            },
            cancellationReasons: {
                type: Array,
                required: true,
            },
            displaySubcustomers: {
                type: Boolean,
                required: false,
                default: false,
            },
            subcustomersOrders: {
                type: Array,
                required: false,
                default: () => [],
            },
            readOnly: {
                type: Boolean,
                required: false,
                default: false,
            },
            forceByUnit: {
                type: Boolean,
                required: false,
                default: false,
            },
            displayAddMissingProductsModal: {
                type: Boolean,
                required: false,
                default: true,
            },
            displayPickupPointsNames: {
                type: Boolean,
                required: false,
                default: false,
            },
        },

        setup() {
            const {
                deleting,
                deleted,
                errorsDeleting,
                doDelete,
            } = useDeleteRequest();

            return {
                deleting,
                deleted,
                errorsDeleting,
                doDelete,
            };
        },

        mounted() {
            jQuery('.dropdown-toggle').dropdown({
                boundary: 'window',
            });
        },

        data() {
            return {
                errorsSending: null,
            };
        },

        methods: {
            preparationBgClass,

            preparationBorderClass,

            resetErrorsSending() {
                this.errorsSending = null;
            },

            resetErrorsDeleting() {
                this.errorsDeleting = null;
            },

            onAddPreparedErrors(errors) {
                this.errorsSending = errors;
            },

            onAddPreparedSubmitted() {
                this.errorsSending = null;
            },

            async removePrepared(id) {
                const response = await this.doDelete('/espace-employes/commandes/preparation/commande/' + this.order.id + '/produits-prepares/' + id);

                if (!this.errorsDeleting) {
                    if (!this.$root.echoConnectedWihoutErrors) {
                        window.location.reload();
                    }
                }
            },

            async removeMissing(id) {
                const response = await this.doDelete('/espace-employes/commandes/preparation/commande/' + this.order.id + '/produits-manquants/' + id);

                if (!this.errorsDeleting) {
                    if (!this.$root.echoConnectedWihoutErrors) {
                        window.location.reload();
                    }
                }
            },

            formatWeight(weight) {
                if (weight === null) {
                    return 'Poids non précisé';
                }
                if (weight) {
                    return weight.toString().replace('.', ',') + ' kg';
                }
            },

            getSubcustomerOrderRefToDisplay(subcustomerWithOrder) {
                return (!subcustomerWithOrder.order.ref || subcustomerWithOrder.order.ref.startsWith('('))
                    ? subcustomerWithOrder.name
                    : ('n°' + subcustomerWithOrder.order.ref);
            },

            pluralize,
        },

        computed: {
            subcustomersOrdersByPickupPoint() {
                const ordersByPickupPoint = {};
                for (const order of this.subcustomersOrders) {
                    const pickupPoint = order.subcustomer?.meta?.pickup_point || "N/C";
                    if (!ordersByPickupPoint.hasOwnProperty(pickupPoint)) {
                        ordersByPickupPoint[pickupPoint] = [];
                    }
                    ordersByPickupPoint[pickupPoint].push(order);
                }
                return ordersByPickupPoint;
            },

            counters() {
                return this.preparationStatus.preparation_counters;
            },

            isWeightNeeded() {
                return (this.order.final_customer_type === 'store' || this.order.final_customer_type === 'pickup_point') && this.product.unit === 'kg' && !this.forceByUnit;
            },

            orderEditable,

            orderProductsQuantitiesEditable,

            orderHasSubcustomers() {
                return this.subcustomersOrders && this.subcustomersOrders.length > 0;
            },

            titleNoSubOrderNumber() {
              return 'Cette commande a été importée avant la fin de la vente alors qu\'elle n\'avait pas encore de numéro.\n'+
                  'Merci de refaire un import après la fin de la vente pour obtenir son numéro.\n'+
                  'Si la préparation a commencé, vous devrez annuler la préparation de chaque produit pour pouvoir refaire un import.';
            },

            subcustomersWithOrderById() {
                let subcustomers = {};
                for (const order of this.subcustomersOrders) {
                    subcustomers[order.subcustomer.id] = {
                        ...order.subcustomer,
                        order: order,
                    }
                }
                return subcustomers;
            },

            nbMissingByOrderRef() {
                let missingByRef = {};
                for (const missingProductEntry of this.order.missing_products) {
                    if (missingProductEntry.sku == this.product.sku) {
                        const cust = this.subcustomersWithOrderById[missingProductEntry.pivot.fk_subcustomer];
                        if (cust) {
                            missingByRef[cust.order.ref] = (missingByRef[cust.order.ref] || 0) + missingProductEntry.pivot.quantity;
                        }
                    }
                }
                return missingByRef;
            },

            orderIsPickupPointProduction() {
                return this.order.final_customer_type === 'pickup_point';
            },

            returnedQuantity() {
                if (this.order.returned_products) {
                    const returnedProduct = _.find(this.order.returned_products, {sku: this.product.sku});
                    if (returnedProduct) {
                        return returnedProduct.pivot.quantity;
                    }
                }
                return 0;
            },
        },
    }
</script>

<style lang="scss" scoped>
    .preparation-product {
        min-width: 350px;

        .card-body {
            display: flex;
            flex-direction: column;
        }

        .preparation-product-header {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: baseline;
            font-size: 1.6rem;
            margin-bottom: 16px;

            .preparation-product-ordered, .preparation-product-prepared {
                border: 2px solid black;
                border-radius: 3px;
                min-height: 48px;
                min-width: 48px;
                padding: 0 7px;
                line-height: 48px;
                text-align: center;
                flex-shrink: 0;
            }

            .preparation-product-name-pricetype {
                text-align: center;
                flex-shrink: 1;
                margin: 0 7px;

                .preparation-product-pricetype {
                    color: #6c757d;
                    font-size: 1rem;
                }
            }
        }

        .preparation-product-inputs {
            flex-grow: 2;
            margin-bottom: 16px;

            table {
                width: 100%;
                padding: 20px;
            }

            .preparation-product-input {
                td {
                    border-bottom: 1px solid #979797;
                    border-top: 1px solid #979797;
                    text-align: center;
                    padding: 5px 5px;
                    width: 50%;
                }
                td[colspan="2"] {
                    width: 100%;
                }
                td:last-of-type {
                    width: 0;
                }
            }
        }

        .preparation-product-returned {
            display: inline-block;
            text-align: center;
            vertical-align: middle;
            border: 1px solid #af5353;
            border-radius: 0.25rem;
            color: #af5353;
            padding: 0.375rem 0.75rem;
            font-size: 0.9rem;
            line-height: 1.6;
        }

        .list-group-item-suborder-ref {

        }

        .list-group-item-suborder-name {
            flex-grow: 1;
            margin-left: 9px;
        }

        .list-group-item-suborder-quantity {
            font-size: 16px;
        }
    }
</style>
